import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import ClientLayout from "../components/layout/ClientLayout";
import axiosInstance from "../../../utils/axiosInstance";

const STATUS_LABEL = { 0: "Pending", 1: "Approved", 2: "Rejected" };
const STATUS_COLOR = {
  0: { background: "#fff3cd", color: "#856404", border: "1px solid #ffc107" },
  1: { background: "#d1f2e8", color: "#0a6b45", border: "1px solid #02b181" },
  2: { background: "#fde8e8", color: "#a31515", border: "1px solid #e74c3c" },
};

function formatDate(val) {
  if (!val) return "";
  return new Date(val).toLocaleString();
}

const AutoWalletMyWithdrawals = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/wallet/my-withdrawal-requests");
      setRecords(res.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  return (
    <ClientLayout showLoader={false}>
      <style>{css}</style>
      <div className="myr-page">

        <div className="myr-title">
          Withdrawal Requests
          <Link to="/auto-wallet/withdrawal" className="myr-back-link">← Back</Link>
        </div>

        <div className="myr-container">
          {loading ? (
            <div className="myr-empty">Loading...</div>
          ) : error ? (
            <div className="myr-error">{error}</div>
          ) : records.length === 0 ? (
            <div className="myr-empty">No withdrawal requests found.</div>
          ) : (
            records.map((row) => (
              <div key={row.id} className="myr-card">
                <div className="myr-row">
                  <span className="myr-label">ID</span>
                  <span className="myr-value mono">
                    {row.display_id != null ? `withdraw-${row.display_id}` : "—"}
                  </span>
                </div>
                <div className="myr-row">
                  <span className="myr-label">Amount</span>
                  <span className="myr-value amount">
                    {Number(row.amount || 0).toLocaleString()}
                  </span>
                </div>
                <div className="myr-row">
                  <span className="myr-label">Payment</span>
                  <span className="myr-value">{row.pay_mode}</span>
                </div>
                {row.bep20_address ? (
                  <div className="myr-row">
                    <span className="myr-label">BEP20</span>
                    <span className="myr-value mono">{row.bep20_address}</span>
                  </div>
                ) : (
                  <>
                    {row.account_title && (
                      <div className="myr-row">
                        <span className="myr-label">Title</span>
                        <span className="myr-value">{row.account_title}</span>
                      </div>
                    )}
                    {row.bank_name && (
                      <div className="myr-row">
                        <span className="myr-label">Bank</span>
                        <span className="myr-value">{row.bank_name}</span>
                      </div>
                    )}
                    {row.account_number && (
                      <div className="myr-row">
                        <span className="myr-label">Account</span>
                        <span className="myr-value mono">{row.account_number}</span>
                      </div>
                    )}
                  </>
                )}
                <div className="myr-row">
                  <span className="myr-label">Date</span>
                  <span className="myr-value">{formatDate(row.date)}</span>
                </div>
                <div className="myr-row">
                  <span className="myr-label">Status</span>
                  <span className="myr-badge" style={STATUS_COLOR[row.status] || STATUS_COLOR[0]}>
                    {STATUS_LABEL[row.status] ?? "Unknown"}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

      </div>
    </ClientLayout>
  );
};

const css = `
  .myr-page {
    background: #eef0f3;
    min-height: 100vh;
    padding-bottom: 40px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }

  .myr-title {
    position: relative;
    text-align: center;
    background: #3d6b8b;
    color: #fff;
    font-size: 17px;
    font-weight: 600;
    padding: 12px 16px;
    letter-spacing: 0.5px;
  }
  .myr-back-link {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    font-weight: 600;
    color: #f0c060;
    text-decoration: none;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.25);
    border-radius: 20px;
    padding: 4px 10px;
    white-space: nowrap;
  }
  .myr-back-link:hover { color: #ffe9a0; text-decoration: none; background: rgba(255,255,255,0.22); }

  .myr-container { padding: 14px 12px; }

  .myr-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.07);
    padding: 14px 16px;
    margin-bottom: 12px;
  }
  .myr-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 0;
    border-bottom: 1px solid #f0f0f0;
  }
  .myr-row:last-child { border-bottom: none; }
  .myr-label {
    font-size: 12px;
    color: #888;
    font-weight: 500;
    min-width: 70px;
  }
  .myr-value {
    font-size: 13px;
    color: #333;
    font-weight: 500;
    text-align: right;
    word-break: break-all;
    max-width: 70%;
  }
  .myr-value.amount { color: #e8951a; font-weight: 700; font-size: 15px; }
  .myr-value.mono   { font-family: monospace; font-size: 12px; }
  .myr-badge {
    font-size: 11px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 20px;
    letter-spacing: 0.3px;
  }
  .myr-empty {
    text-align: center;
    padding: 40px 0;
    color: #999;
    font-size: 14px;
  }
  .myr-error {
    text-align: center;
    padding: 20px;
    color: #c0392b;
    font-size: 14px;
    background: #fef0f0;
    border-radius: 8px;
    margin-bottom: 12px;
  }
`;

export default AutoWalletMyWithdrawals;
